/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { Button, Image, Text, Textarea } from '@/shared/ui';
import { AccentPanel } from '@/shared/ui/components/composed/accent-panel';

export type TranslateFn = (key: string, opts?: Record<string, unknown>) => string;

const WARNING_SURFACE = 'color-mix(in srgb, var(--status-warning) 12%, white)';

/**
 * Renders a loading placeholder shown while an edit panel fetches the post
 * content.
 *
 * @param props.t - Translation function for the loading label.
 * @returns The centered pulsing loading indicator.
 */
export function EditLoading({ t }: { t: TranslateFn }) {
  return (
    <div className="flex items-center justify-center py-(--space-6) gap-(--space-2)" style={{ color: 'var(--text-tertiary)' }}>
      <Text as="span" className="text-xs animate-pulse">{t('publications.loadingPostContent')}</Text>
    </div>
  );
}

interface ThumbProps {
  src: string;
  index: number;
  pending: number | null;
  disabled: boolean;
  isLastImage: boolean;
  onRemove: (index: number) => void;
  t: TranslateFn;
}

/**
 * Renders a single editable post-image thumbnail with a hover remove button,
 * highlighting the pending-removal state and disabling removal of the last image.
 *
 * @param props - Image source, index, pending-removal index, disabled/last-image
 *   flags, remove handler, and translator.
 * @returns The thumbnail element.
 */
function EditThumb({ src, index, pending, disabled, isLastImage, onRemove, t }: ThumbProps) {
  const isPending = pending === index;
  const borderColor = isPending ? 'var(--status-error-text)' : 'var(--border-subtle)';
  const opacity = pending !== null && !isPending ? 0.5 : 1;
  const title = isLastImage ? t('publications.lastImageWarning') : t('publications.actions.removeImage');
  return (
    <div className="relative group" style={isPending ? { outline: '2px solid var(--status-error-text)', borderRadius: 'var(--radius-md)' } : {}}>
      <Image src={src} alt="" className="w-20 h-20 object-cover rounded-(--radius-md) border" style={{ borderColor, opacity }} />
      <Button
        type="button" variant="none" disableSize onClick={() => onRemove(index)} disabled={disabled}
        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
        style={{ background: 'var(--status-error-text)', color: 'var(--text-inverse)', fontSize: 'var(--text-micro)', lineHeight: 1 }}
        title={title} aria-label={t('publications.actions.removeImage')} data-testid={`remove-image-btn-${index}`}
      >
        &times;
      </Button>
    </div>
  );
}

interface GridProps {
  images: string[];
  pending: number | null;
  isLastImage: boolean;
  onRemove: (index: number) => void;
  t: TranslateFn;
}

/**
 * Renders a thumbnail grid of editable post images, wiring per-image remove
 * controls and disabling removal when only the last image remains.
 *
 * @param props.images - Image source URLs.
 * @param props.pending - Index pending removal, or null.
 * @param props.isLastImage - Whether only one image remains.
 * @param props.onRemove - Callback invoked with the image index to remove.
 * @param props.t - Translation function for labels.
 * @returns The image thumbnail grid.
 */
export function EditImageGrid({ images, pending, isLastImage, onRemove, t }: GridProps) {
  return (
    <div className="flex gap-(--space-2) flex-wrap">
      {images.map((src, i) => (
        <EditThumb key={i} src={src} index={i} pending={pending} isLastImage={isLastImage} disabled={(pending !== null && pending !== i) || isLastImage} onRemove={onRemove} t={t} />
      ))}
    </div>
  );
}

/**
 * Renders an inline warning banner (icon + message) for portals that recreate
 * the post on edit.
 *
 * @param props.messageKey - Translation key for the warning message.
 * @param props.testId - Test id for the banner element.
 * @param props.t - Translation function for the message.
 * @returns The warning banner.
 */
export function EditWarningBanner({ messageKey, testId, t }: { messageKey: string; testId: string; t: TranslateFn }) {
  return (
    <AccentPanel
      accent="var(--status-warning)"
      surface={WARNING_SURFACE}
      className="mb-(--space-2) text-xs"
      style={{ color: 'var(--status-warning-text)' }}
      testId={testId}
    >
      <div className="flex items-start gap-(--space-2)">
        <ExclamationTriangleIcon width={12} height={12} className="mt-(--space-0-5) shrink-0" />
        <Text as="span">{t(messageKey)}</Text>
      </div>
    </AccentPanel>
  );
}

interface ConfirmProps {
  onCancel: () => void;
  onConfirm: () => void;
  t: TranslateFn;
}

/**
 * Renders a confirmation prompt shown before removing an image on a
 * recreate-on-edit portal, with cancel and confirm actions.
 *
 * @param props.onCancel - Callback to dismiss the prompt.
 * @param props.onConfirm - Callback to confirm the removal.
 * @param props.t - Translation function for labels.
 * @returns The confirmation prompt.
 */
export function ImageRemoveConfirm({ onCancel, onConfirm, t }: ConfirmProps) {
  return (
    <AccentPanel accent="var(--status-warning)" surface={WARNING_SURFACE} testId="image-remove-confirm">
      <div className="flex items-start gap-(--space-2) text-xs" style={{ color: 'var(--status-warning-text)' }}>
        <ExclamationTriangleIcon width={12} height={12} className="mt-(--space-0-5) shrink-0" />
        <Text as="span">{t('publications.imageChangeWarning')}</Text>
      </div>
      <div className="flex gap-(--space-2)">
        <Button type="button" variant="none" disableSize onClick={onCancel} className="flex-1 py-(--space-1-5) text-xs font-semibold rounded-(--radius-md) border cursor-pointer hover:opacity-80" style={{ background: 'transparent', color: 'var(--text-secondary)', borderColor: 'var(--border-subtle)' }} data-testid="image-remove-cancel">
          {t('publications.actions.cancel')}
        </Button>
        <Button type="button" variant="none" disableSize onClick={onConfirm} className="flex-1 py-(--space-1-5) text-xs font-semibold rounded-(--radius-md) cursor-pointer hover:opacity-90" style={{ background: 'var(--status-error-text)', color: 'var(--text-inverse)', border: 'none' }} data-testid="image-remove-confirm-btn">
          {t('publications.imageRemoveConfirm')}
        </Button>
      </div>
    </AccentPanel>
  );
}

interface EditTextProps {
  value: string;
  rowKey: string;
  onChange: (v: string) => void;
  t: TranslateFn;
}

/**
 * Renders the editable post-text area, showing an empty-content hint when the
 * value is blank.
 *
 * @param props.value - Current post text.
 * @param props.rowKey - Row key used for the test id.
 * @param props.onChange - Callback receiving the updated text.
 * @param props.t - Translation function for placeholder and hint.
 * @returns The editable text area with hint.
 */
export function EditText({ value, rowKey, onChange, t }: EditTextProps) {
  return (
    <div>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('publications.editPlaceholder')}
        rows={5}
        className="text-xs resize-none leading-relaxed"
        data-testid={`edit-textarea-${rowKey}`}
        autoFocus
      />
      {!value && (
        <Text as="p" className="text-[length:var(--text-micro)] mt-(--space-1)" style={{ color: 'var(--text-tertiary)' }}>
          {t('publications.editEmptyHint')}
        </Text>
      )}
    </div>
  );
}

interface ActionsProps {
  rowKey: string;
  isSaving: boolean;
  saveDisabled: boolean;
  onSave: () => void;
  onCancel: () => void;
  t: TranslateFn;
}

/**
 * Renders the cancel/save action buttons for the inline post editor, disabling
 * save while saving or when save is not allowed.
 *
 * @param props.rowKey - Row key used for the test id.
 * @param props.isSaving - Whether a save is in flight.
 * @param props.saveDisabled - Whether the save button is disabled.
 * @param props.onSave - Callback invoked to save.
 * @param props.onCancel - Callback invoked to cancel.
 * @param props.t - Translation function for labels.
 * @returns The editor action buttons.
 */
export function EditActions({ rowKey, isSaving, saveDisabled, onSave, onCancel, t }: ActionsProps) {
  return (
    <div className="flex gap-(--space-2) justify-end">
      <Button type="button" variant="none" disableSize onClick={onCancel} className="px-(--space-3) py-(--space-1-5) text-xs font-semibold rounded-(--radius-md) border cursor-pointer hover:opacity-80" style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-subtle)', background: 'transparent' }}>
        {t('publications.actions.cancel')}
      </Button>
      <Button type="button" disabled={saveDisabled} onClick={onSave} className="px-(--space-3) py-(--space-1-5) text-xs font-semibold rounded-(--radius-md) cursor-pointer disabled:opacity-50 hover:opacity-90" style={{ background: 'var(--seed-base)', color: 'var(--text-inverse)' }} data-testid={`edit-save-btn-${rowKey}`}>
        {isSaving ? '...' : t('publications.actions.saveEdit')}
      </Button>
    </div>
  );
}
